import React, { useMemo } from 'react'
import { Deal } from './crmData'
import { useCrm } from './useCrm'
import { getModuleStatuses, getPlatformUsage } from './crmApi'

/**
 * Platform section — how much this customer is ACTUALLY using WeQuote.
 *
 * Reframed in Phase 1.4. The original design showed "hours per module", which
 * WeQuote cannot supply: they run no session timers, so time-in-app has no data
 * source and never will. What their API does report — and pushes to us — is
 * COUNTS of activity over an explicit period (quotes created, invoices raised,
 * snags logged…) plus an engagement block (logins, active days, active users,
 * last active). So that is what this page shows:
 *
 *   • Hero          — total activity in the period + active days (was: hours)
 *   • Engagement    — logins · active days · active users · last active
 *   • Feature areas — the six we already track, each with the counts behind it
 *                     and the delta vs baseline. Metrics WeQuote adds later that
 *                     we know nothing about still appear, under "Other".
 *
 * Baseline: inside the first month of WeQuote's ACTIVATION date we compare to
 * peers on the same plan tier ("vs peers on your plan"); after that, to the
 * account's own previous periods ("vs your last periods").
 *
 * Three honest states, never a blank page:
 *   1. real usage    — the latest pushed period (POST /api/webhooks/wequote/usage)
 *   2. presence only — no usage pushed yet, but active-sessions says the account
 *                      is alive. We show that and say so, rather than inventing
 *                      activity numbers.
 *   3. sample        — endpoint unavailable → deterministic mock, clearly labelled.
 */

// Mirrors USAGE_METRIC_GROUPS in packages/api/src/crm/config.ts. Used for the
// sample fallback only — the real grouping comes from the API.
const FEATURE_AREAS: { key: string; label: string; metrics: string[] }[] = [
  { key: 'quoting', label: 'Quoting', metrics: ['quotes_created', 'quotes_sent', 'quotes_accepted', 'change_orders_created'] },
  { key: 'accounts', label: 'Accounts', metrics: ['invoices_raised'] },
  { key: 'workhub', label: 'WorkHub', metrics: ['projects_created', 'jobs_booked', 'tasks_created'] },
  { key: 'snagging', label: 'Snagging', metrics: ['snags_logged'] },
  { key: 'inventory', label: 'Inventory', metrics: ['goods_in', 'goods_out', 'stock_variations'] },
  { key: 'procurement', label: 'Procurement', metrics: ['purchase_orders_raised', 'requests_created'] },
]

interface MetricRow { key: string; label: string; value: number; baseline: number }
interface Group { key: string; label: string; total: number; baselineTotal: number; metrics: MetricRow[] }
interface Engagement { logins?: number; active_days?: number; active_users?: number; last_active_at?: string | null }
interface Presence { lastSeenAt?: string; lastSeenDays?: number | null; activeUsers?: number }
interface Usage {
  hasUsage: boolean
  /** True when this period is SEEDED demo data (Phase 1.5) — always badged, never passed off as real. */
  seeded?: boolean
  periodStart?: string
  periodEnd?: string
  plan?: string | null
  planTier?: string
  baseline?: 'cohort' | 'own' | 'none'
  total?: number
  baselineTotal?: number
  engagement?: Engagement
  baselineEngagement?: Engagement
  groups?: Group[]
  sparkline?: number[]
  periodCount?: number
  presence?: Presence | null
}

type Live = 'live' | 'setup' | 'off'
interface StatusInfo { moduleKey: string; label: string; setupState: string; live: boolean }
const FALLBACK_STATUSES: StatusInfo[] = FEATURE_AREAS.map((m) => ({ moduleKey: m.key, label: m.label, setupState: 'none', live: false }))

const LIVE_COLOR: Record<Live, string> = { live: '#22C55E', setup: '#94A3B8', off: '#CBD5E1' }
const LIVE_LABEL: Record<Live, string> = { live: 'Live & running', setup: 'Set up, not used', off: 'Not live' }

const DAY = 86400000
const round1 = (n: number) => Math.round(n * 10) / 10
const humanize = (k: string) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

// NO fabricated fallback. If the usage endpoint is unreachable or has no data for this account,
// the page says exactly that. (The old buildSampleUsage generated plausible-looking numbers from
// the deal id — removed: real data or an honest empty state, never invented activity.)
const EMPTY_USAGE: Usage = { hasUsage: false, presence: null }

const fmtDate = (s?: string) => (s ? new Date(s).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '—')
const daysBetween = (a?: string, b?: string) => (a && b ? Math.max(1, Math.round((new Date(b).getTime() - new Date(a).getTime()) / DAY)) : 7)
const agoLabel = (iso?: string | null, days?: number | null) => {
  const d = days ?? (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / DAY) : null)
  if (d == null || Number.isNaN(d)) return '—'
  return d <= 0 ? 'Today' : d === 1 ? 'Yesterday' : `${d}d ago`
}

const Sparkline: React.FC<{ points: number[]; color: string }> = ({ points, color }) => {
  if (points.length < 2) return null
  const max = Math.max(1, ...points)
  const w = 120, h = 34
  const step = w / (points.length - 1)
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${(h - (p / max) * (h - 4) - 2).toFixed(1)}`).join(' ')
  return (
    <svg width={w} height={h} className="overflow-visible" aria-hidden>
      <path d={d} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={w} cy={h - (points[points.length - 1] / max) * (h - 4) - 2} r={2.5} fill={color} />
    </svg>
  )
}

const Stat: React.FC<{ label: string; value: React.ReactNode; sub?: string; tone?: string }> = ({ label, value, sub, tone }) => (
  <div className="flex-1 min-w-[104px] px-3 py-2.5 rounded-lg bg-surface-secondary">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary">{label}</p>
    <p className="text-lg font-bold leading-tight mt-0.5 text-txt-primary" style={tone ? { color: tone } : undefined}>{value}</p>
    {sub && <p className="text-[10px] text-txt-tertiary mt-0.5">{sub}</p>}
  </div>
)

export const DealPlatform: React.FC<{ deal: Deal }> = ({ deal }) => {
  // Setup's live flags remain the source of truth for which areas are live.
  const { data: statuses } = useCrm<StatusInfo[]>(() => getModuleStatuses(deal.id) as Promise<StatusInfo[]>, FALLBACK_STATUSES, [deal.id])
  const { data, live } = useCrm<Usage>(() => getPlatformUsage(deal.id) as Promise<Usage>, EMPTY_USAGE, [deal.id])

  const statusByKey = useMemo(() => {
    const m: Record<string, Live> = {}
    ;(statuses.length ? statuses : FALLBACK_STATUSES).forEach((s) => { m[s.moduleKey] = s.live ? 'live' : s.setupState !== 'none' ? 'setup' : 'off' })
    return m
  }, [statuses])

  // ── No usage data — whether the endpoint answered "none yet" or was unreachable.
  // Say exactly that; never fabricate activity.
  if (!data.hasUsage) {
    const p = data.presence
    return (
      <div className="max-w-[720px] space-y-4">
        <div className="rounded-xl border border-border p-5">
          <p className="text-sm font-semibold text-txt-primary">No activity data for this account yet</p>
          <p className="text-xs text-txt-tertiary mt-1 leading-relaxed">
            WeQuote hasn’t pushed a usage period for this account. Activity counts and the feature-area
            breakdown appear here as soon as the first period lands.
          </p>
        </div>
        {p && (
          <div className="flex gap-2 flex-wrap">
            <Stat
              label="Last seen"
              value={agoLabel(p.lastSeenAt, p.lastSeenDays)}
              sub="from live sessions"
              tone={(p.lastSeenDays ?? 99) <= 2 ? '#16a34a' : (p.lastSeenDays ?? 99) >= 14 ? '#dc2626' : '#d97706'}
            />
            <Stat label="Users online" value={p.activeUsers ?? 0} sub="at last check" />
          </div>
        )}
      </div>
    )
  }

  const groups = data.groups || []
  const eng = data.engagement || {}
  const baseEng = data.baselineEngagement || {}
  const total = data.total || 0
  const baselineTotal = data.baselineTotal || 0
  const delta = round1(total - baselineTotal)
  const deltaPct = baselineTotal ? Math.round((delta / baselineTotal) * 100) : 0
  const up = delta >= 0
  const hasBaseline = !!data.baseline && data.baseline !== 'none'
  const deltaColor = !hasBaseline ? '#94A3B8' : up ? '#16a34a' : deltaPct <= -20 ? '#dc2626' : '#d97706'
  const periodDays = daysBetween(data.periodStart, data.periodEnd)
  const activeDays = eng.active_days ?? 0
  const baselineLabel =
    data.baseline === 'cohort'
      ? `vs peers on your plan${data.plan ? ` (${data.plan})` : ''}`
      : data.baseline === 'own'
        ? 'vs your last periods'
        : 'first period — no baseline yet'
  const maxGroup = Math.max(1, ...groups.map((g) => g.total))

  return (
    <div className="max-w-[720px] space-y-5">
      {live && data.seeded && (
        <p className="text-[11px] px-3 py-2 rounded-lg" style={{ background: '#FEF3C7', color: '#92400E' }}>
          <span className="font-bold">Seeded demo data.</span> These figures were generated so the chain is
          demonstrable before WeQuote’s usage push is live. They disappear automatically the moment a real
          period arrives for this account.
        </p>
      )}

      {/* Hero — activity, not hours */}
      <div className="rounded-xl border border-border p-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary">
            Platform activity · {fmtDate(data.periodStart)}–{fmtDate(data.periodEnd)}
          </p>
          <div className="flex items-end gap-3 mt-1">
            <span className="text-4xl font-extrabold text-txt-primary leading-none">{total.toLocaleString()}</span>
            <span className="text-sm text-txt-tertiary mb-1">actions</span>
            {hasBaseline && (
              <span className="text-sm font-bold mb-1" style={{ color: deltaColor }}>
                {up ? '▲' : '▼'} {up ? '+' : ''}{delta} · {up ? '+' : ''}{deltaPct}%
              </span>
            )}
          </div>
          <p className="text-[11px] text-txt-tertiary mt-1">
            {baselineLabel}{hasBaseline ? ` · baseline ${baselineTotal}` : ''} · active {activeDays} of {periodDays} days
          </p>
        </div>
        <Sparkline points={data.sparkline && data.sparkline.length > 1 ? data.sparkline : [baselineTotal, total]} color={deltaColor} />
      </div>

      {/* Engagement — the honest replacement for "hours" */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary mb-2">Engagement</p>
        <div className="flex gap-2 flex-wrap">
          <Stat
            label="Active days"
            value={`${activeDays}/${periodDays}`}
            sub={baseEng.active_days != null ? `baseline ${baseEng.active_days}` : undefined}
            tone={activeDays === 0 ? '#dc2626' : activeDays <= 1 ? '#d97706' : '#16a34a'}
          />
          <Stat label="Logins" value={eng.logins ?? 0} sub={baseEng.logins != null ? `baseline ${baseEng.logins}` : undefined} />
          <Stat label="Active users" value={eng.active_users ?? data.presence?.activeUsers ?? 0} sub={baseEng.active_users != null ? `baseline ${baseEng.active_users}` : undefined} />
          <Stat
            label="Last active"
            value={agoLabel(data.presence?.lastSeenAt ?? eng.last_active_at, data.presence?.lastSeenDays)}
            sub={data.presence ? 'live session' : 'this period'}
          />
        </div>
        {activeDays === 0 && (
          <p className="text-[11px] mt-2" style={{ color: '#dc2626' }}>
            Not active at all in this period — the strongest churn signal we have.
          </p>
        )}
      </div>

      {/* Feature areas */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary mb-2">By feature area</p>
        <div className="rounded-xl border border-border overflow-hidden">
          {groups.length === 0 && <p className="px-4 py-4 text-xs text-txt-tertiary">No activity recorded in this period.</p>}
          {groups.map((g) => {
            const state: Live = g.key === 'other' ? 'live' : statusByKey[g.key] || 'off'
            const gDelta = round1(g.total - g.baselineTotal)
            const flat = Math.abs(gDelta) < 0.5
            const pct = total ? Math.round((g.total / total) * 100) : 0
            return (
              <div key={g.key} className={`px-4 py-3 border-b border-border last:border-0 ${g.total === 0 && state === 'off' ? 'opacity-55' : ''}`}>
                <div className="flex items-center gap-3">
                  {g.key !== 'other' && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: LIVE_COLOR[state] }} title={LIVE_LABEL[state]} />}
                  <span className="text-sm font-medium text-txt-primary w-28 shrink-0">{g.label}</span>
                  <div className="flex-1 h-2 rounded-full bg-surface-tertiary overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(g.total / maxGroup) * 100}%`, background: state === 'live' ? '#22C55E' : '#94A3B8' }} />
                  </div>
                  <span className="text-sm font-semibold text-txt-primary w-14 text-right shrink-0">{g.total.toLocaleString()}</span>
                  <span className="text-[11px] text-txt-tertiary w-10 text-right shrink-0">{pct}%</span>
                  <span
                    className="w-6 text-right shrink-0 text-sm font-bold"
                    style={{ color: !hasBaseline || flat ? '#94A3B8' : gDelta > 0 ? '#16a34a' : '#dc2626' }}
                    title={hasBaseline ? `${gDelta > 0 ? '+' : ''}${gDelta} vs baseline ${g.baselineTotal}` : 'no baseline yet'}
                  >
                    {!hasBaseline || flat ? '→' : gDelta > 0 ? '▲' : '▼'}
                  </span>
                </div>
                {/* the counts behind the area — this is what WeQuote actually reports */}
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 ml-[22px]">
                  {g.metrics.map((m) => (
                    <span key={m.key} className="text-[11px] text-txt-tertiary">
                      {m.label} <span className="font-semibold text-txt-secondary">{m.value.toLocaleString()}</span>
                      {hasBaseline && m.baseline > 0 && <span className="opacity-70"> / {m.baseline}</span>}
                    </span>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
        <div className="flex items-center gap-4 mt-2 text-[11px] text-txt-tertiary flex-wrap">
          {(['live', 'setup', 'off'] as Live[]).map((s) => (
            <span key={s} className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: LIVE_COLOR[s] }} />{LIVE_LABEL[s]}</span>
          ))}
          {hasBaseline && <span className="opacity-80">value / baseline</span>}
        </div>
      </div>
    </div>
  )
}
